import { useState, useEffect } from 'react'
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Typography,
  IconButton,
  FormControlLabel,
  Switch,
} from '@mui/material'
import { CloudUpload, Delete } from '@mui/icons-material'
import { Modal, Button } from '@components/common'
import api from '@services/api'
import { toast } from 'react-toastify'

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
]

const FAULT_TYPE_OPTIONS = [
  { value: 'breakdown', label: 'Breakdown' },
  { value: 'malfunction', label: 'Malfunction' },
  { value: 'damage', label: 'Physical Damage' },
  { value: 'safety', label: 'Safety Hazard' },
  { value: 'performance', label: 'Performance Issue' },
  { value: 'calibration', label: 'Calibration Issue' },
  { value: 'electrical', label: 'Electrical Issue' },
  { value: 'mechanical', label: 'Mechanical Issue' },
  { value: 'software', label: 'Software Issue' },
  { value: 'other', label: 'Other' },
]

const initialFormData = {
  asset: '',
  title: '',
  description: '',
  fault_type: 'malfunction',
  priority: 'medium',
  location: '',
  room_number: '',
  is_equipment_usable: true,
  notes: '',
}

const FaultReportForm = ({ open, onClose, asset, onSuccess }) => {
  const [formData, setFormData] = useState(initialFormData)
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({})
  const [assets, setAssets] = useState([])
  const [locations, setLocations] = useState([])

  // Image upload state
  const [imageFiles, setImageFiles] = useState({ image_1: null, image_2: null, image_3: null, image_4: null })
  const [imagePreviews, setImagePreviews] = useState({ image_1: null, image_2: null, image_3: null, image_4: null })

  useEffect(() => {
    if (open) {
      fetchOptions()
      if (asset) {
        // Pre-fill asset if provided
        setFormData({
          ...initialFormData,
          asset: asset.id,
          location: asset.location?.id || '',
          room_number: asset.room_number || '',
        })
      } else {
        setFormData(initialFormData)
      }
      setImageFiles({ image_1: null, image_2: null, image_3: null, image_4: null })
      setImagePreviews({ image_1: null, image_2: null, image_3: null, image_4: null })
      setErrors({})
    }
  }, [open, asset])

  const fetchOptions = async () => {
    try {
      const [assetsRes, locationsRes] = await Promise.all([
        api.get('/assets/assets/'),
        api.get('/assets/locations/'),
      ])
      setAssets(assetsRes.data.results || assetsRes.data)
      setLocations(locationsRes.data.results || locationsRes.data)
    } catch (error) {
      console.error('Error fetching options:', error)
    }
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }))
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }
  }

  const handleAssetChange = (e) => {
    const assetId = e.target.value
    setFormData((prev) => ({ ...prev, asset: assetId }))

    // Auto-fill location from selected asset
    const selectedAsset = assets.find(a => a.id === assetId)
    if (selectedAsset) {
      setFormData((prev) => ({
        ...prev,
        asset: assetId,
        location: selectedAsset.location || '',
        room_number: selectedAsset.room_number || '',
      }))
    }
  }

  const handleImageChange = (e, fieldName) => {
    const file = e.target.files[0]
    if (file) {
      setImageFiles(prev => ({ ...prev, [fieldName]: file }))
      setImagePreviews(prev => ({ ...prev, [fieldName]: URL.createObjectURL(file) }))
    }
  }

  const handleRemoveImage = (fieldName) => {
    setImageFiles(prev => ({ ...prev, [fieldName]: null }))
    setImagePreviews(prev => ({ ...prev, [fieldName]: null }))
  }

  const validate = () => {
    const newErrors = {}
    if (!formData.asset) newErrors.asset = 'Please select an item'
    if (!formData.title.trim()) newErrors.title = 'Fault description is required'
    if (!formData.description.trim()) newErrors.description = 'Details are required'
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validate()) return

    setLoading(true)
    try {
      const submitData = new FormData()

      // Add form fields
      Object.entries(formData).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          submitData.append(key, value)
        }
      })

      // Add images
      Object.entries(imageFiles).forEach(([key, file]) => {
        if (file) {
          submitData.append(key, file)
        }
      })

      await api.post('/assets/fault-reports/', submitData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })

      toast.success('Fault report submitted successfully')
      onSuccess?.()
      onClose()
    } catch (error) {
      console.error('Error submitting fault report:', error)
      if (error.response?.data) {
        const backendErrors = {}
        Object.entries(error.response.data).forEach(([key, value]) => {
          backendErrors[key] = Array.isArray(value) ? value[0] : value
        })
        setErrors(backendErrors)
      } else {
        toast.error('Failed to submit fault report')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Report a Fault"
      maxWidth="md"
      actions={
        <>
          <Button variant="outlined" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={loading}>
            Submit Report
          </Button>
        </>
      }
    >
      <Grid container spacing={3}>
        {/* Item (Asset) Selection */}
        <Grid item xs={12}>
          <FormControl fullWidth error={Boolean(errors.asset)} required>
            <InputLabel>Item</InputLabel>
            <Select
              name="asset"
              value={formData.asset}
              onChange={handleAssetChange}
              label="Item"
              disabled={Boolean(asset)}
            >
              {assets.map((a) => (
                <MenuItem key={a.id} value={a.id}>
                  {a.name} {a.asset_tag && `(${a.asset_tag})`}
                </MenuItem>
              ))}
            </Select>
            {errors.asset && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                {errors.asset}
              </Typography>
            )}
          </FormControl>
        </Grid>

        {/* Fault Description (Title) */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Fault Description"
            name="title"
            value={formData.title}
            onChange={handleChange}
            error={Boolean(errors.title)}
            helperText={errors.title}
            required
            placeholder="Brief description of the fault"
          />
        </Grid>

        {/* Detailed Description */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Fault Details"
            name="description"
            value={formData.description}
            onChange={handleChange}
            error={Boolean(errors.description)}
            helperText={errors.description}
            multiline
            rows={3}
            required
            placeholder="Provide detailed information about the fault..."
          />
        </Grid>

        {/* Fault Type and Priority */}
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Fault Type</InputLabel>
            <Select
              name="fault_type"
              value={formData.fault_type}
              onChange={handleChange}
              label="Fault Type"
            >
              {FAULT_TYPE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Priority</InputLabel>
            <Select
              name="priority"
              value={formData.priority}
              onChange={handleChange}
              label="Priority"
            >
              {PRIORITY_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Location */}
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Location</InputLabel>
            <Select
              name="location"
              value={formData.location}
              onChange={handleChange}
              label="Location"
            >
              <MenuItem value="">None</MenuItem>
              {locations.map((loc) => (
                <MenuItem key={loc.id} value={loc.id}>
                  {loc.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Room Number"
            name="room_number"
            value={formData.room_number}
            onChange={handleChange}
          />
        </Grid>

        {/* Equipment Usable */}
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.is_equipment_usable}
                onChange={handleChange}
                name="is_equipment_usable"
              />
            }
            label="Equipment is still usable despite the fault"
          />
        </Grid>

        {/* Additional Notes */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Additional Notes"
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            multiline
            rows={2}
          />
        </Grid>

        {/* Images */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Attach Images (Optional)
          </Typography>
          <Box display="flex" gap={2} flexWrap="wrap">
            {['image_1', 'image_2', 'image_3', 'image_4'].map((fieldName, index) => (
              <Box
                key={fieldName}
                sx={{
                  width: 100,
                  height: 100,
                  border: '2px dashed',
                  borderColor: 'divider',
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
                }}
              >
                {imagePreviews[fieldName] ? (
                  <>
                    <Box
                      component="img"
                      src={imagePreviews[fieldName]}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveImage(fieldName)}
                      sx={{
                        position: 'absolute',
                        top: 2,
                        right: 2,
                        bgcolor: 'background.paper',
                        '&:hover': { bgcolor: 'error.light', color: 'white' },
                      }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </>
                ) : (
                  <>
                    <CloudUpload sx={{ color: 'text.disabled' }} />
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageChange(e, fieldName)}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        opacity: 0,
                        cursor: 'pointer',
                      }}
                    />
                  </>
                )}
              </Box>
            ))}
          </Box>
        </Grid>
      </Grid>
    </Modal>
  )
}

export default FaultReportForm
